import React, { useState } from 'react';
import { Button, IconButton, Popover, Tag, colors } from '@neshane/blueberry';

/**
 * NewFeatureSlideshow - a "What's new" style popover that walks the user through
 * a list of feature slides with a slide-dots indicator and a Next button, ending
 * with the provided call-to-action on the last slide.
 *
 * @param {Array<{title: React.ReactNode, content?: React.ReactNode}>} slides - Ordered feature slides to present.
 * @param {React.ReactNode} [callToAction] - Content rendered in place of the Next button on the last slide.
 * @param {string} [newLabel='New'] - Label shown on the trigger Tag.
 * @param {string} [nextLabel='Next'] - Label shown on the Next button.
 * @param {string} [closeLabel='Close'] - Accessible label for the close IconButton.
 */
function NewFeatureSlideshow({
    slides,
    callToAction,
    newLabel = 'New',
    nextLabel = 'Next',
    closeLabel = 'Close',
}) {
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

    const currentSlide = slides[currentSlideIndex];

    return (
        <div className="flex flex-row items-center gap-8">
            <Popover
                placement="top-start"
                renderTrigger={(triggerProps) => (
                    <Tag
                        variant="purple"
                        {...triggerProps}
                        onClick={(event) => {
                            event.preventDefault();
                            if (!triggerProps['aria-expanded']) {
                                triggerProps.onClick();
                            }
                        }}
                        onMouseOver={() => {
                            if (!triggerProps['aria-expanded']) {
                                triggerProps.onClick();
                            }
                        }}
                        className="cursor-pointer"
                    >
                        {newLabel}
                    </Tag>
                )}
            >
                {(onClose) => (
                    <div className="w-[366px] rounded-4 bg-primary-brand-default text-complementary shadow-l">
                        <IconButton
                            onClick={onClose}
                            iconColor={colors.backgroundPrimaryDefault}
                            iconName="cross"
                            variant="tertiaryNeutral"
                            className="absolute right-0 top-0"
                            aria-label={closeLabel}
                        />

                        <div className="flex flex-col gap-16 p-16">
                            <div className="pr-24 title-m">{currentSlide.title}</div>

                            {currentSlide.content && (
                                <div className="mb-16 body-m">{currentSlide.content}</div>
                            )}

                            <div className="flex flex-row items-center justify-between">
                                <div className="flex flex-row gap-8">
                                    {slides.map((_, index) => {
                                        return (
                                            <div
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={index}
                                                className={`h-8 w-8 rounded-[9999px] ${
                                                    index === currentSlideIndex
                                                        ? 'bg-primary-default'
                                                        : 'bg-secondary-brand-default'
                                                }`}
                                            />
                                        );
                                    })}
                                </div>

                                {currentSlideIndex !== slides.length - 1 ? (
                                    <Button
                                        text={nextLabel}
                                        variant="secondaryNeutral"
                                        onClick={() => {
                                            setCurrentSlideIndex(currentSlideIndex + 1);
                                        }}
                                    />
                                ) : (
                                    callToAction
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Popover>
        </div>
    );
}

export default NewFeatureSlideshow;